import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, X } from 'lucide-react';
import EnhancedCameraCapture from '../EnhancedCameraCapture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dataUrlToFile(dataUrl, name) {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

async function uploadEvidence(projectId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await axios.post(`${API}/projects/${projectId}/qs/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data.evidenceId;
}

// Reusable field camera + upload control. Reuses ReasonVisionX EnhancedCameraCapture (back camera).
export default function CameraEvidence({ projectId, evidenceIds = [], onChange, testId = 'camera-evidence' }) {
  const [cam, setCam] = useState(false);
  const [busy, setBusy] = useState(false);

  const onCapture = async (dataUrl) => {
    setCam(false);
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      onChange([...(evidenceIds || []), dataUrl]);
      toast.success('Evidence captured (will upload when back online)');
      return;
    }
    setBusy(true);
    try {
      const id = await uploadEvidence(projectId, dataUrlToFile(dataUrl, `capture-${Date.now()}.jpg`));
      onChange([...(evidenceIds || []), id]);
      toast.success('Evidence captured');
    } catch (e) { toast.error('Upload failed'); }
    setBusy(false);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const reader = new FileReader();
      reader.onload = () => { onChange([...(evidenceIds || []), reader.result]); toast.success('Evidence queued (will upload when back online)'); };
      reader.readAsDataURL(f); e.target.value = ''; return;
    }
    setBusy(true);
    try { const id = await uploadEvidence(projectId, f); onChange([...(evidenceIds || []), id]); toast.success('Evidence uploaded'); }
    catch (err) { toast.error('Upload failed'); }
    setBusy(false); e.target.value = '';
  };
  const remove = (id) => onChange((evidenceIds || []).filter((x) => x !== id));

  const chipLabel = (id) => (typeof id === 'string' && id.startsWith('data:')) ? 'offline photo' : id.split('/').pop().slice(0, 10);

  return (
    <div data-testid={testId}>
      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={() => setCam(true)} disabled={busy} data-testid={`${testId}-camera-btn`} className="flex items-center gap-1 text-xs px-2 py-1.5 bg-slate-800 text-white rounded-lg">
          <Camera className="w-3.5 h-3.5" /> Camera
        </button>
        <label className="flex items-center gap-1 text-xs px-2 py-1.5 border rounded-lg cursor-pointer" data-testid={`${testId}-upload-label`}>
          <Upload className="w-3.5 h-3.5" /> Upload
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid={`${testId}-file-input`} />
        </label>
        {busy && <span className="text-xs text-slate-400 self-center">Uploading…</span>}
      </div>
      {(evidenceIds || []).length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {evidenceIds.map((id) => (
            <span key={id} className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`${testId}-chip`}>
              {chipLabel(id)} <button onClick={() => remove(id)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {cam && (
        <div className="fixed inset-0 z-[60] bg-black" data-testid={`${testId}-camera-modal`}>
          <EnhancedCameraCapture onCapture={onCapture} onCancel={() => setCam(false)} />
        </div>
      )}
    </div>
  );
}

export { uploadEvidence };
